import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import SEOMeta from "@/components/SEOMeta";
import SchemaMarkup from "@/components/SchemaMarkup";
import FAQ from "@/components/FAQ";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { 
  Hammer, 
  Shield, 
  CheckCircle2,
  Clock,
  Phone,
  Mail,
  MapPin,
  Award,
  Ruler,
  Palette,
  Lock,
  TreeDeciduous
} from "lucide-react";
import heroImage from "@/assets/deck-fence-hero.jpg";
import deckConstructionImage from "@/assets/deck-construction-ontario.jpg";
import fenceInstallationImage from "@/assets/fence-installation-ontario.jpg";
import compositeDeckImage from "@/assets/composite-deck-luxury.jpg";
import vinylFenceImage from "@/assets/vinyl-fence-backyard.jpg";

const DeckFenceServices = () => {
  const faqData = [
    {
      question: "How much does a new deck cost in the GTA?",
      answer: "Deck costs vary based on size, materials, and complexity. A basic pressure-treated wood deck typically ranges from $15-$35 per square foot, while composite decking runs $30-$60 per square foot. Multi-level decks with custom features cost more. Contact us for a free detailed quote for your specific project in Toronto, Hamilton, or Niagara Region."
    },
    {
      question: "What type of fencing is best for privacy in Ontario?",
      answer: "For maximum privacy, we recommend 6-foot solid board fences (wood or vinyl), horizontal slat fencing, or vinyl privacy panels. Cedar and pressure-treated wood are popular for natural aesthetics, while vinyl offers low maintenance. We can help you choose the best option based on your needs, budget, and property requirements."
    },
    {
      question: "How long does deck construction take?",
      answer: "Most residential decks take 3-7 days to complete, depending on size and complexity. Simple ground-level decks may be finished in 2-3 days, while multi-level decks with stairs and built-in features can take 1-2 weeks. We provide a detailed timeline during your consultation."
    },
    {
      question: "Do I need a permit for deck or fence installation?",
      answer: "In most Ontario municipalities, decks over 24 inches high and fences over 6 feet require building permits. Permit requirements vary by city. Blue Knight Contracting handles all permit applications and ensures your project meets local building codes in Toronto, Hamilton, Niagara, and throughout the GTA."
    },
    {
      question: "What's the difference between wood and composite decking?",
      answer: "Wood decking (cedar, pressure-treated) is more affordable upfront and offers natural beauty but requires regular staining and maintenance. Composite decking costs more initially but requires minimal maintenance, resists fading and staining, and lasts 25+ years. We help you choose based on your budget, aesthetic preferences, and maintenance preferences."
    },
    {
      question: "How long will my new fence last?",
      answer: "Fence lifespan depends on material and maintenance. Pressure-treated wood fences last 15-20 years with proper care, cedar fences 20-30 years, vinyl fences 20-30+ years with minimal maintenance, and aluminum/steel fences 25-50+ years. We use quality materials and proper installation techniques to maximize your fence's longevity."
    }
  ];

  const deckServices = [
    {
      title: "Custom Deck Design & Build",
      description: "Complete deck construction services from design to completion",
      icon: Hammer,
      features: [
        "Custom deck design consultation",
        "Multi-level and raised deck construction",
        "Ground-level patio decks",
        "Deck stairs and railing installation",
        "Built-in seating and planters",
        "Deck lighting and electrical"
      ]
    },
    {
      title: "Wood Decking",
      description: "Natural beauty with pressure-treated lumber and premium cedar",
      icon: TreeDeciduous,
      features: [
        "Pressure-treated pine decking",
        "Premium cedar deck construction",
        "Exotic hardwood options (Ipe, Tigerwood)",
        "Custom wood staining and sealing",
        "Wood deck restoration",
        "Regular maintenance programs"
      ]
    },
    {
      title: "Composite & PVC Decking",
      description: "Low-maintenance, long-lasting synthetic decking materials",
      icon: Palette,
      features: [
        "Trex composite decking installation",
        "TimberTech and Azek systems",
        "PVC vinyl decking options",
        "Matching railing systems",
        "Hidden fastener installation",
        "25+ year warranties"
      ]
    }
  ];

  const fenceServices = [
    {
      title: "Privacy Fencing",
      description: "Complete privacy with solid board and panel fencing",
      icon: Lock,
      features: [
        "6-foot and 8-foot privacy fences",
        "Solid board construction",
        "Horizontal slat fencing",
        "Cedar and pressure-treated options",
        "Vinyl privacy panels",
        "Custom gate installation"
      ]
    },
    {
      title: "Decorative Fencing",
      description: "Enhance curb appeal with attractive fence designs",
      icon: Palette,
      features: [
        "Picket fence installation",
        "Lattice-top fencing",
        "Shadowbox style fences",
        "Decorative post caps and finials",
        "Custom paint and stain colors",
        "Ornamental aluminum fencing"
      ]
    },
    {
      title: "Fence Repair & Replacement",
      description: "Restore or replace damaged fencing",
      icon: Shield,
      features: [
        "Fence panel replacement",
        "Post repair and replacement",
        "Gate repair and hardware",
        "Full fence replacement",
        "Storm damage repairs",
        "Fence staining and sealing"
      ]
    }
  ];

  const benefits = [
    {
      icon: Award,
      title: "Expert Craftsmanship",
      description: "Over 20 years of deck and fence building experience with attention to every detail"
    },
    {
      icon: Shield,
      title: "Quality Materials",
      description: "We use only premium lumber, composite materials, and hardware that meet building codes"
    },
    {
      icon: Clock,
      title: "On-Time Completion",
      description: "Projects completed on schedule with minimal disruption to your daily routine"
    },
    {
      icon: Ruler,
      title: "Custom Designs",
      description: "Tailored solutions that match your home's architecture and your lifestyle needs"
    }
  ];

  const serviceAreas = [
    { name: "Toronto", description: "Downtown, North York, Scarborough, Etobicoke" },
    { name: "Hamilton", description: "Downtown, Stoney Creek, Dundas, Ancaster" },
    { name: "Niagara Region", description: "St. Catharines, Niagara Falls, Welland, Grimsby" },
    { name: "GTA", description: "Mississauga, Brampton, Markham, Vaughan, Oakville" }
  ];

  return (
    <>
      <SEOMeta 
        title="Deck & Fence Installation Toronto, Hamilton, Niagara | Custom Decks & Fencing GTA"
        description="Professional deck and fence installation in Toronto, Hamilton & Niagara Region. Custom wood & composite decks, privacy fences, vinyl fencing. Licensed & insured. Free quotes."
        keywords="deck installation Toronto, fence installation Hamilton, deck builder Niagara, custom deck GTA, privacy fence Toronto, composite deck Hamilton, vinyl fence Niagara, deck contractor Ontario"
        canonicalUrl="https://blueknightcontracting.ca/services/deck-fence"
        ogImage={heroImage}
      />
      
      <SchemaMarkup
        type="Service"
        data={{
          serviceType: "Deck and Fence Installation Services",
          serviceName: "Professional Deck Building & Fence Installation",
          services: [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Custom Deck Construction Toronto",
                "description": "Expert custom deck design and construction using wood, composite, and PVC materials."
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Privacy Fence Installation Hamilton",
                "description": "Professional privacy fence installation with wood, vinyl, and composite materials."
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Deck and Fence Services Niagara Region",
                "description": "Complete outdoor living solutions with custom decks and quality fencing."
              }
            }
          ]
        }}
      />

      <div className="min-h-screen bg-background">
        <Header />

        {/* Hero Section */}
        <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${heroImage})`,
            }}
          />
          <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Custom Decks & Professional Fencing
            </h1>
            <p className="text-xl md:text-2xl mb-8">
              Transform Your Outdoor Space | Toronto, Hamilton & Niagara Region
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="default" asChild>
                <Link to="/contact">Get Free Quote</Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="bg-white/10 text-white border-white hover:bg-white/20">
                <a href="tel:4165552583">Call (416) 555-2583</a>
              </Button>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-6">
                  Ontario's Trusted Deck & Fence Specialists
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Blue Knight Contracting has been building beautiful decks and installing quality fencing throughout the Greater Toronto Area, Hamilton, and Niagara Region for over 20 years. Our experienced team combines expert craftsmanship with premium materials to create outdoor spaces that enhance your property's value and your family's lifestyle.
                </p>
                <p className="text-lg text-muted-foreground mb-6">
                  Whether you're looking for a stunning multi-level deck, a private backyard fence, or a complete outdoor renovation, we deliver superior results on time and on budget. All our work is fully licensed, insured, and backed by comprehensive warranties.
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                    <span className="font-semibold">Licensed & Insured</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                    <span className="font-semibold">20+ Years Experience</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                    <span className="font-semibold">Premium Materials</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <Card key={index}>
                    <CardHeader>
                      <benefit.icon className="h-8 w-8 text-primary mb-2" />
                      <CardTitle className="text-lg">{benefit.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{benefit.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Deck Services */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Professional Deck Building Services</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Custom deck design and construction with wood, composite, and PVC materials
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {deckServices.map((service, index) => (
                <Card key={index} className="h-full">
                  <CardHeader>
                    <service.icon className="h-12 w-12 text-primary mb-4" />
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Deck Showcase */}
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={deckConstructionImage}
                    alt="Custom multi-level deck construction in Ontario with cedar wood and railings"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold mb-3">Traditional Wood Decks</h3>
                  <p className="text-muted-foreground mb-4">
                    Beautiful natural wood decks using premium cedar and pressure-treated lumber. Perfect for homeowners who love the authentic look and feel of real wood.
                  </p>
                  <Button asChild>
                    <Link to="/contact">Request Quote</Link>
                  </Button>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={compositeDeckImage}
                    alt="Luxury composite deck with glass railings and LED lighting in Ontario"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold mb-3">Composite & PVC Decks</h3>
                  <p className="text-muted-foreground mb-4">
                    Low-maintenance composite decking that looks stunning and lasts for decades. Choose from a wide range of colors and textures with minimal upkeep required.
                  </p>
                  <Button asChild>
                    <Link to="/contact">Request Quote</Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Fence Services */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Expert Fence Installation Services</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Privacy fences, decorative fencing, and custom gate installation
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {fenceServices.map((service, index) => (
                <Card key={index} className="h-full">
                  <CardHeader>
                    <service.icon className="h-12 w-12 text-primary mb-4" />
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Fence Showcase */}
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={fenceInstallationImage}
                    alt="Professional horizontal wood fence installation in Ontario backyard"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold mb-3">Wood Privacy Fencing</h3>
                  <p className="text-muted-foreground mb-4">
                    Classic wood fencing for maximum privacy and natural beauty. Available in cedar, pressure-treated pine, and various stain colors to match your home.
                  </p>
                  <Button asChild>
                    <Link to="/contact">Request Quote</Link>
                  </Button>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={vinylFenceImage}
                    alt="Low-maintenance white vinyl privacy fence in Ontario backyard"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold mb-3">Vinyl & Composite Fencing</h3>
                  <p className="text-muted-foreground mb-4">
                    Maintenance-free vinyl and composite fencing that never needs painting or staining. Perfect for busy homeowners who want beautiful, long-lasting results.
                  </p>
                  <Button asChild>
                    <Link to="/contact">Request Quote</Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Our Deck & Fence Installation Process</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Professional service from consultation to completion
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              {[
                {
                  step: "01",
                  title: "Free Consultation",
                  description: "We visit your property to discuss your vision, take measurements, and provide expert recommendations."
                },
                {
                  step: "02",
                  title: "Custom Design",
                  description: "Receive a detailed design plan with material options, cost breakdown, and project timeline."
                },
                {
                  step: "03",
                  title: "Expert Installation",
                  description: "Our skilled team builds your deck or fence using quality materials and proven construction techniques."
                },
                {
                  step: "04",
                  title: "Final Inspection",
                  description: "Thorough walkthrough, cleanup, and care instructions to ensure your complete satisfaction."
                }
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="inline-block bg-primary text-primary-foreground rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                  <p className="text-muted-foreground">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Serving Southern Ontario</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Professional deck and fence installation throughout the GTA, Hamilton, and Niagara Region
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {serviceAreas.map((area, index) => (
                <Card key={index} className="text-center">
                  <CardHeader>
                    <MapPin className="h-8 w-8 text-primary mx-auto mb-2" />
                    <CardTitle className="text-xl">{area.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{area.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-primary text-primary-foreground">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">
              Ready to Build Your Dream Deck or Fence?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Get a free consultation and quote today. Transform your outdoor space with Blue Knight Contracting.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" asChild>
                <Link to="/contact">Request Free Quote</Link>
              </Button>
              <Button size="lg" variant="outline" className="bg-white/10 border-white text-white hover:bg-white/20" asChild>
                <a href="tel:4165552583">
                  <Phone className="mr-2 h-5 w-5" />
                  (416) 555-2583
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <FAQ 
              title="Frequently Asked Questions About Decks & Fences"
              description="Get answers to common questions about deck construction and fence installation"
              faqs={faqData}
            />
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-4xl mx-auto">
            <Card>
              <CardHeader className="text-center">
                <CardTitle className="text-2xl mb-2">
                  Contact Blue Knight Contracting Today
                </CardTitle>
                <CardDescription className="text-base">
                  Professional deck and fence services in Toronto, Hamilton, Niagara & GTA
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-6 text-center">
                  <div>
                    <Phone className="h-8 w-8 text-primary mx-auto mb-2" />
                    <p className="font-semibold">Call Us</p>
                    <a href="tel:4165552583" className="text-primary hover:underline">
                      (416) 555-2583
                    </a>
                  </div>
                  <div>
                    <Mail className="h-8 w-8 text-primary mx-auto mb-2" />
                    <p className="font-semibold">Email Us</p>
                    <a href="mailto:info@blueknightcontracting.ca" className="text-primary hover:underline">
                      info@blueknightcontracting.ca
                    </a>
                  </div>
                  <div>
                    <Hammer className="h-8 w-8 text-primary mx-auto mb-2" />
                    <p className="font-semibold">Get Started</p>
                    <Link to="/contact" className="text-primary hover:underline">
                      Free Consultation
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default DeckFenceServices;
